import { useEffect, useMemo, useRef, useState } from "react";
import { GoogleMap, InfoWindow, Marker } from "@react-google-maps/api";
import { FiX } from "react-icons/fi";
import useGoogleMapsLoader from "../../../../Hooks/useGoogleMapsLoader";
import { formatIeComplaintDateTime, getIeComplaintCloseTimePosition } from "./ieComplaintUtils";

const containerStyle = { width: "100%", height: "100%" };

const mapOptions = {
  zoomControl: true,
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: false,
  clickableIcons: false,
};

const mapWrapperStyle = {
  height: "65vh",
  minHeight: 380,
  width: "100%",
  borderRadius: 12,
  overflow: "hidden",
  border: "1px solid #e2e8f0",
  background: "#f8fafc",
};

const infoRowGridStyle = {
  display: "grid",
  gridTemplateColumns: "120px 1fr",
  gap: 8,
};

const infoLabelStyle = {
  fontSize: 11,
  fontWeight: 700,
  color: "#64748b",
};

const infoValueStyle = {
  fontSize: 12,
  fontWeight: 600,
  color: "#1e293b",
  wordBreak: "break-word",
};

const safeString = (value) => {
  if (value == null || value === "") return "—";
  return typeof value === "string" ? value : String(value);
};

const normalizeCloseTimePosition = (row) => getIeComplaintCloseTimePosition(row);

const InfoRow = ({ label, value }) => (
  <div style={infoRowGridStyle}>
    <div style={infoLabelStyle}>{label}</div>
    <div style={infoValueStyle}>{value}</div>
  </div>
);

const MapInfoWindow = ({ position, showInfo, onClose, className, children }) => {
  if (!showInfo || !position || !window.google?.maps) return null;

  return (
    <InfoWindow
      position={position}
      onCloseClick={onClose}
      options={{
        pixelOffset: new window.google.maps.Size(0, -36),
        headerDisabled: true,
      }}
      onLoad={(infoWindow) => {
        window.google.maps.event.addListener(infoWindow, "domready", () => {
          const content = document.querySelector(`.${className}`);
          const shell = content?.closest(".gm-style-iw");
          const chrome = shell?.querySelector(".gm-style-iw-ch");
          const body = shell?.querySelector(".gm-style-iw-c");
          if (chrome) {
            chrome.style.paddingTop = "0";
            chrome.style.minHeight = "0";
          }
          if (body) body.style.padding = "6px 10px 10px";
        });
      }}
    >
      <div
        className={className}
        style={{
          position: "relative",
          minWidth: 280,
          maxWidth: 480,
          paddingRight: 24,
        }}
      >
        <button
          type="button"
          onClick={onClose}
          title="Close"
          style={{
            position: "absolute",
            right: 0,
            top: 0,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            height: 28,
            width: 28,
            border: "none",
            borderRadius: 8,
            background: "transparent",
            color: "#64748b",
            cursor: "pointer",
          }}
        >
          <FiX size={15} />
        </button>
        <div style={{ display: "grid", gap: 8 }}>{children}</div>
      </div>
    </InfoWindow>
  );
};

export function IeComplaintCloseTimeMapContent({ row }) {
  const { isLoaded, loadError } = useGoogleMapsLoader();
  const mapRef = useRef(null);
  const [showInfo, setShowInfo] = useState(true);

  const position = useMemo(() => normalizeCloseTimePosition(row), [row]);

  const name = safeString(row?.complainantNo ?? row?.colonyName);
  const location = safeString(
    row?.closeTimeLocation ?? row?.cAddress ?? row?.location ?? row?.fAddress
  );
  const latitude = position ? position.lat.toFixed(6) : "—";
  const longitude = position ? position.lng.toFixed(6) : "—";
  const closeTime = formatIeComplaintDateTime(
    row?.complaintClosureOn ?? row?.modifiedDate
  );

  useEffect(() => {
    if (!mapRef.current || !position) return;
    mapRef.current.panTo(position);
  }, [position?.lat, position?.lng]);

  if (!row || !position) {
    return (
      <div style={{ padding: 16, fontSize: 14, color: "#475569" }}>
        Close time coordinates are not available for this complaint.
      </div>
    );
  }

  if (loadError) {
    return (
      <div style={{ padding: 16, fontSize: 14, color: "#dc2626" }}>
        Unable to load map.
      </div>
    );
  }

  return (
    <div style={mapWrapperStyle}>
      <div style={{ position: "relative", height: "100%", width: "100%" }}>
        {!isLoaded ? (
          <div
            style={{
              height: "100%",
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 14,
              color: "#64748b",
            }}
          >
            Loading map...
          </div>
        ) : (
          <GoogleMap
            mapContainerStyle={containerStyle}
            center={position}
            zoom={18}
            options={mapOptions}
            onLoad={(map) => {
              mapRef.current = map;
              map.panTo(position);
              if (map.getZoom() < 18) map.setZoom(18);
            }}
          >
            <Marker position={position} onClick={() => setShowInfo(true)} />
            <MapInfoWindow
              position={position}
              showInfo={showInfo}
              onClose={() => setShowInfo(false)}
              className="ie-complaint-close-map-infowindow"
            >
              <InfoRow label="Name :" value={name} />
              <InfoRow label="Location :" value={location} />
              <InfoRow label="Latitude :" value={latitude} />
              <InfoRow label="Longitude :" value={longitude} />
              <InfoRow label="Close Time :" value={closeTime} />
            </MapInfoWindow>
          </GoogleMap>
        )}
      </div>
    </div>
  );
}
